import { fieldLabel, LogicalField, TemplateFieldOption } from '../fieldCatalog';
import { LinkedField, LinkedFieldKey, SlideObject, createObjectId, defaultLocks } from '../types';

export interface BoundField {
  productId: string;
  key: LinkedFieldKey;
  source: 'direct' | 'local';
  label: string;
}

export function upsertLinkedField(fields: LinkedField[] | undefined, next: LinkedField): LinkedField[] {
  return [...(fields || []).filter((field) => field.key !== next.key), next];
}

export function attachDefaultLinkedFields(product: SlideObject, values: { title?: string; articleNumber?: string }): SlideObject {
  return {
    ...product,
    linkedFields: [
      { key: 'title', source: 'direct', value: values.title || product.name || '' },
      { key: 'articleNumber', source: 'direct', value: values.articleNumber || '' },
    ],
  };
}

export function createProductLinkedTexts(
  product: SlideObject,
  values: { title?: string; articleNumber?: string },
  styles?: {
    heading?: { fontFamily: string; fontSize: number; fontWeight: number; textTransform: 'none' | 'uppercase'; lineHeight: number };
    body?: { fontFamily: string; fontSize: number; fontWeight: number; textTransform: 'none' | 'uppercase'; lineHeight: number };
    color?: string;
  },
): SlideObject[] {
  const heading = styles?.heading || {
    fontFamily: 'Kapra Neue',
    fontSize: 72,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    lineHeight: 1,
  };
  const body = styles?.body || {
    fontFamily: 'Akkurat Std',
    fontSize: 26,
    fontWeight: 300,
    textTransform: 'none' as const,
    lineHeight: 1.2,
  };
  const color = styles?.color || '#1B3967';
  return [
    {
      id: createObjectId('title'),
      type: 'text',
      name: 'Titel',
      x: product.x,
      y: product.y + product.h + 16,
      w: product.w,
      h: 56,
      z: product.z + 1,
      locks: defaultLocks(),
      content: values.title || '',
      linkedFrom: { objectId: product.id, key: 'title' },
      style: {
        fontFamily: heading.fontFamily,
        fontSize: heading.fontSize,
        fontWeight: heading.fontWeight,
        color,
        textTransform: heading.textTransform,
        lineHeight: heading.lineHeight,
      },
    },
    {
      id: createObjectId('sku'),
      type: 'text',
      name: 'Artikelnummer',
      x: product.x,
      y: product.y + product.h + 80,
      w: product.w,
      h: 40,
      z: product.z + 2,
      locks: defaultLocks(),
      content: values.articleNumber || '',
      linkedFrom: { objectId: product.id, key: 'articleNumber' },
      style: {
        fontFamily: body.fontFamily,
        fontSize: body.fontSize,
        fontWeight: body.fontWeight,
        color: '#111111',
        textTransform: body.textTransform,
        lineHeight: body.lineHeight,
      },
    },
  ];
}

export function setLinkedFieldSource(
  objects: SlideObject[],
  productId: string,
  key: LinkedFieldKey,
  source: 'direct' | 'local',
  localValue?: string
): SlideObject[] {
  return objects.map((object) => {
    if (object.id === productId && object.type === 'product') {
      const current = (object.linkedFields || []).find((field) => field.key === key);
      const value = source === 'local'
        ? (localValue != null ? localValue : current?.value || '')
        : (current?.value || localValue || '');
      return { ...object, linkedFields: upsertLinkedField(object.linkedFields, { key, source, value }) };
    }
    return object;
  });
}

export function boundFieldText(objects: SlideObject[], productId: string, key: LinkedFieldKey): SlideObject | undefined {
  return objects.find((object) => object.linkedFrom?.objectId === productId && object.linkedFrom.key === key);
}

export function resolveBoundField(
  objects: SlideObject[],
  object: SlideObject | null | undefined,
  catalogLabels: TemplateFieldOption[] = [],
  fields?: LogicalField[],
): BoundField | null {
  const link = object?.linkedFrom;
  if (!link) return null;
  const product = objects.find((item) => item.id === link.objectId && item.type === 'product');
  if (!product) return null;
  const field = (product.linkedFields || []).find((item) => item.key === link.key);
  if (!field) return null;
  return {
    productId: product.id,
    key: field.key,
    source: field.source,
    label: fieldLabel(field.key, catalogLabels, fields),
  };
}
